import { Loader, Modal } from '@mantine/core';
import Select from '@xver/components/Atoms/Select/Select';
import { notifications } from '@mantine/notifications';
import { IconBrandGithub, IconExclamationCircle, IconFileCode } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type { CreateContainerSourceRequest } from '@xver/apis/v1/kufer/containers/model';
import type {
    AlreadyAuthorizedResponse,
    AuthRedirectResponse,
    GetDockerfileResponse,
} from '@xver/apis/v1/kufer/github/model';
import Button from '@xver/components/Atoms/Button/Button';
import Editor from '@xver/components/Atoms/Editor/Editor';
import InputField from '@xver/components/Atoms/InputField/InputField';
import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGitHubAuthQuery } from '../../../../../hooks/GitHubHooks/useGitHubAuthQuery';
import { useGitHubRepositoriesQuery } from '../hooks/useGitHubRepositoriesQuery';
import { getErrorMessage, getStatusCode, isGitHubTokenInvalid } from '@xver/utils/error';

export interface GitHubSourceFormValue {
    github_owner: string;
    github_repository: string;
    github_branch: string;
    dockerfile_path: string;
}

interface GitHubSourceFormProps {
    value: GitHubSourceFormValue;
    onChange: (value: GitHubSourceFormValue) => void;
    error?: string;
}

const SELECT_STYLES = {
    input: {
        backgroundColor: 'var(--surface-2)',
        color: 'var(--fg)',
    },
    option: {
        backgroundColor: 'var(--surface-2)',
        color: 'var(--fg)',
    },
    dropdown: {
        backgroundColor: 'var(--surface-2)',
        borderColor: 'var(--line)',
    },
};

export const DEFAULT_GITHUB_SOURCE_VALUE: GitHubSourceFormValue = {
    github_owner: '',
    github_repository: '',
    github_branch: '',
    dockerfile_path: 'Dockerfile',
};

export function toCreateSourceRequest(value: GitHubSourceFormValue): CreateContainerSourceRequest {
    return {
        type: 'github',
        github_owner: value.github_owner,
        github_repository: value.github_repository,
        github_branch: value.github_branch,
        dockerfile_path: value.dockerfile_path.trim() || 'Dockerfile',
    };
}

export default function GitHubSourceForm({ value, onChange, error }: GitHubSourceFormProps) {
    const ownerId = useId();
    const repositoryId = useId();
    const errorId = useId();
    const { t } = useTranslation('containerEngine');
    const {
        data: auth,
        isLoading: isAuthLoading,
        isError: isAuthError,
        error: authError,
        refetch: refetchAuth,
    } = useGitHubAuthQuery();

    const isConnected = !!auth && !isAuthError;
    const notConnected = isAuthError && getStatusCode(authError) === 404;

    const {
        data: repositories,
        isLoading: isRepositoriesLoading,
        isError: isRepositoriesError,
        error: repositoriesError,
        refetch: refetchRepositories,
    } = useGitHubRepositoriesQuery({ per_page: 100 }, { enabled: isConnected });

    // A dead GitHub token: reconnecting is the only way out. A 401 unauthenticated is an expired
    // session instead, which the kufer client already sends to sign-in.
    const needsReconnect = isGitHubTokenInvalid(repositoriesError);

    const ownerOptions = useMemo(() => {
        if (!repositories?.repositories) return [];
        const owners = new Set(
            repositories.repositories.map((repo) => repo.full_name.split('/')[0])
        );
        return [...owners].sort().map((owner) => ({ value: owner, label: owner }));
    }, [repositories]);

    const repositoryOptions = useMemo(() => {
        if (!repositories?.repositories || !value.github_owner) return [];
        return repositories.repositories
            .filter((repo) => repo.full_name.split('/')[0] === value.github_owner)
            .map((repo) => ({ value: repo.name, label: repo.name }));
    }, [repositories, value.github_owner]);

    const connectMutation = useMutation({
        mutationFn: Endpoints.v1.Kufer.GitHub.getAuthorizeUrl,
        onSuccess: (data) => {
            // eslint-disable-next-line no-console
            console.log('[github connect] response:', data);

            const raw = data as unknown;
            const payload = (
                raw && typeof raw === 'object' && 'data' in (raw as Record<string, unknown>)
                    ? (raw as { data: unknown }).data
                    : raw
            ) as Partial<AlreadyAuthorizedResponse & AuthRedirectResponse>;

            if (payload?.already_authorized) {
                void refetchAuth();
                return;
            }

            const redirect = payload?.redirect_url;
            if (redirect) {
                window.location.href = redirect;
                return;
            }

            // eslint-disable-next-line no-console
            console.error('[github connect] unexpected response shape:', data);
            notifications.show({
                title: t(
                    'github.toast.redirectMissingTitle',
                    'GitHub connect returned no redirect URL'
                ),
                message: t(
                    'github.toast.redirectMissingMessage',
                    'Open DevTools → Network and inspect the response of GET /v1/github/authorize.'
                ),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
        onError: (err: { message?: string; status?: number }) => {
            const detail = [err?.status ? `HTTP ${err.status}` : null, err?.message]
                .filter(Boolean)
                .join(' — ');
            notifications.show({
                title: t('deployment.github.connectFailedTitle', 'GitHub connect failed'),
                message:
                    detail ||
                    t(
                        'deployment.github.connectFailedMessage',
                        'Unable to start the GitHub authorization flow.'
                    ),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    const [dockerfilePreviewOpen, setDockerfilePreviewOpen] = useState(false);
    const [dockerfilePreview, setDockerfilePreview] = useState<GetDockerfileResponse | null>(null);

    const dockerfilePreviewMutation = useMutation({
        mutationFn: async () => {
            if (!value.github_owner || !value.github_repository) {
                throw new Error('Pick a repository before previewing.');
            }
            return Endpoints.v1.Kufer.GitHub.getDockerfile(
                value.github_owner,
                value.github_repository,
                { ref: value.github_branch || undefined }
            );
        },
        onSuccess: (data) => {
            setDockerfilePreview(data);
            setDockerfilePreviewOpen(true);
        },
        onError: (err: { message?: string }) => {
            notifications.show({
                title: t('deployment.github.previewFailedTitle', 'Dockerfile preview failed'),
                message:
                    err?.message ??
                    t(
                        'deployment.github.previewFailedMessage',
                        'Unable to load the Dockerfile from GitHub.'
                    ),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    if (isAuthLoading) {
        return (
            <div className="w-full flex justify-center py-6">
                <Loader size={20} color="var(--fg)" />
            </div>
        );
    }

    if (notConnected || (!isConnected && !isAuthLoading)) {
        return (
            <div className="w-full flex flex-col items-start gap-3 p-4 rounded-md border border-line bg-surface-2">
                <div className="flex items-center gap-2 text-fg">
                    <IconBrandGithub size={20} />
                    <span className="text-base font-medium">
                        {t('deployment.github.connectTitle', 'Connect your GitHub account')}
                    </span>
                </div>
                <p className="text-sm text-fg/68 max-w-2xl">
                    {t(
                        'deployment.github.connectDescription',
                        'Authorize the xver-cloud GitHub App to read your repositories and pull Dockerfiles for builds.'
                    )}
                </p>
                <Button
                    leftIcon={<IconBrandGithub size={16} />}
                    onClick={() => connectMutation.mutate()}
                    isLoading={connectMutation.isPending}
                >
                    {t('deployment.github.connectButton', 'Connect GitHub')}
                </Button>
            </div>
        );
    }

    return (
        <div className="w-full flex flex-col gap-4">
            <div className="w-full flex flex-row items-center gap-3 p-3 rounded-md border border-line bg-surface-2">
                {auth?.github_avatar_url ? (
                    <img
                        src={auth.github_avatar_url}
                        alt={auth.github_username}
                        className="w-8 h-8 rounded-full"
                    />
                ) : (
                    <div className="w-8 h-8 rounded-full bg-surface-3 flex items-center justify-center">
                        <IconBrandGithub size={16} />
                    </div>
                )}
                <div className="flex flex-col">
                    <span className="text-sm text-fg">
                        {t('deployment.github.connectedAs', 'Connected as')}{' '}
                        <strong>{auth?.github_username}</strong>
                    </span>
                    <span className="text-xs text-fg/68">
                        {t('deployment.github.connectedAt', 'Connected at')} {auth?.connected_at}
                    </span>
                </div>
            </div>

            {isRepositoriesError && (
                <div
                    role="alert"
                    className="w-full flex flex-col items-start gap-2 p-3 rounded-md border border-red-500/40 bg-red-500/10"
                >
                    <div className="flex flex-row items-start gap-2">
                        <IconExclamationCircle
                            size={16}
                            className="shrink-0 mt-0.5 text-red-400 light:text-red-700"
                        />
                        <div className="flex flex-col gap-0.5">
                            <p className="text-sm font-medium text-red-300 light:text-red-700">
                                {t(
                                    'deployment.github.loadFailedTitle',
                                    'Could not load your repositories'
                                )}
                            </p>
                            <p className="text-xs text-red-300/90 light:text-red-700">
                                {getErrorMessage(repositoriesError)}
                            </p>
                            {needsReconnect && (
                                <p className="text-xs text-fg/68 mt-1">
                                    {t(
                                        'deployment.github.reconnectHint',
                                        'Your GitHub connection is no longer valid. Reconnect to continue.'
                                    )}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-row items-center gap-2">
                        {needsReconnect ? (
                            <Button
                                size="sm"
                                variant="secondary"
                                leftIcon={<IconBrandGithub size={14} />}
                                onClick={() => connectMutation.mutate()}
                                isLoading={connectMutation.isPending}
                            >
                                {t('deployment.github.reconnect', 'Reconnect GitHub')}
                            </Button>
                        ) : (
                            <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => void refetchRepositories()}
                                isLoading={isRepositoriesLoading}
                            >
                                {t('deployment.github.retry', 'Try again')}
                            </Button>
                        )}
                    </div>
                </div>
            )}

            <div className="w-full flex flex-col gap-1">
                <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                        <label htmlFor={ownerId} className="text-sm font-medium text-fg/85">
                            {t('deployment.github.owner', 'Owner')}
                        </label>
                        <Select
                            id={ownerId}
                            size="md"
                            searchable
                            aria-invalid={error ? true : undefined}
                            aria-describedby={error ? errorId : undefined}
                            placeholder={
                                isRepositoriesLoading
                                    ? t('deployment.github.ownerLoading', 'Loading owners...')
                                    : t('deployment.github.ownerPlaceholder', 'Select an owner')
                            }
                            nothingFoundMessage={t('deployment.github.ownerEmpty', 'No owners')}
                            data={ownerOptions}
                            value={value.github_owner || null}
                            rightSection={
                                isRepositoriesLoading ? (
                                    <Loader size={16} color="var(--fg)" />
                                ) : undefined
                            }
                            onChange={(owner) =>
                                onChange({
                                    ...value,
                                    github_owner: owner ?? '',
                                    github_repository: '',
                                    github_branch: '',
                                })
                            }
                            styles={SELECT_STYLES}
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label htmlFor={repositoryId} className="text-sm font-medium text-fg/85">
                            {t('deployment.github.repository', 'Repository')}
                        </label>
                        <Select
                            id={repositoryId}
                            size="md"
                            searchable
                            aria-invalid={error ? true : undefined}
                            aria-describedby={error ? errorId : undefined}
                            disabled={!value.github_owner || isRepositoriesLoading}
                            placeholder={
                                isRepositoriesLoading
                                    ? t(
                                          'deployment.github.repositoryLoading',
                                          'Loading repositories...'
                                      )
                                    : t(
                                          'deployment.github.repositoryPlaceholder',
                                          'Select a repository'
                                      )
                            }
                            nothingFoundMessage={t(
                                'deployment.github.repositoryEmpty',
                                'No repositories'
                            )}
                            data={repositoryOptions}
                            value={value.github_repository || null}
                            onChange={(repo) => {
                                if (!repo) {
                                    onChange({
                                        ...value,
                                        github_repository: '',
                                        github_branch: '',
                                    });
                                    return;
                                }
                                const selected = repositories?.repositories.find(
                                    (r) => r.full_name === `${value.github_owner}/${repo}`
                                );
                                onChange({
                                    ...value,
                                    github_repository: repo,
                                    github_branch:
                                        selected?.default_branch || value.github_branch || 'main',
                                });
                            }}
                            styles={SELECT_STYLES}
                        />
                    </div>
                </div>
                {error && (
                    <span id={errorId} className="text-xs text-red-400 light:text-red-700 mt-1">
                        {error}
                    </span>
                )}
            </div>

            <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                    size="md"
                    label={<h3 className="text-base">{t('deployment.github.branch', 'Branch')}</h3>}
                    placeholder={t('deployment.github.branchPlaceholder', 'main')}
                    value={value.github_branch}
                    onChange={(e) => onChange({ ...value, github_branch: e.target.value })}
                />
                <InputField
                    size="md"
                    label={
                        <h3 className="text-base">
                            {t('deployment.github.dockerfilePath', 'Dockerfile path')}
                        </h3>
                    }
                    placeholder={t('deployment.github.dockerfilePathPlaceholder', 'Dockerfile')}
                    value={value.dockerfile_path}
                    onChange={(e) => onChange({ ...value, dockerfile_path: e.target.value })}
                    descriptionBottom={t(
                        'deployment.github.dockerfilePathDescription',
                        'Path relative to the repository root.'
                    )}
                />
            </div>

            <div>
                <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<IconFileCode size={16} />}
                    onClick={() => dockerfilePreviewMutation.mutate()}
                    isLoading={dockerfilePreviewMutation.isPending}
                    disabled={
                        !value.github_owner || !value.github_repository || !value.github_branch
                    }
                >
                    {t('deployment.github.previewButton', 'Preview Dockerfile')}
                </Button>
            </div>

            <Modal
                opened={dockerfilePreviewOpen}
                onClose={() => setDockerfilePreviewOpen(false)}
                size="lg"
                centered
                withCloseButton
                title={
                    dockerfilePreview
                        ? `${dockerfilePreview.owner}/${dockerfilePreview.repo}@${
                              dockerfilePreview.ref ?? 'HEAD'
                          }`
                        : t('deployment.github.previewModalTitle', 'Dockerfile preview')
                }
                overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
            >
                {dockerfilePreview && (
                    <Editor
                        readOnly
                        filename={dockerfilePreview.path || 'Dockerfile'}
                        height={420}
                        initialValue={dockerfilePreview.content}
                        enableCopy
                    />
                )}
            </Modal>
        </div>
    );
}
